import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import CancelConfirmDialog from '../../components/cancel-confirm-dialog';
import { showToast } from '../../utils';
import {
  useCreateBOMHardwareMutation,
  useDeleteBOMHardwareMutation,
  useUpdateBOMHardwareMutation,
} from './price-management-api-slice';
import { BOMHardware, PriceVersion } from './types';

export type BOMHardwareModalMode = 'add' | 'edit' | 'view';

export interface BOMHardwareModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  mode: BOMHardwareModalMode;
  version: PriceVersion;
  bomHardware?: BOMHardware | null;
}

const schema = z.object({
  hardwareType: z
    .string()
    .min(1, 'Code is required')
    .max(100, 'Code must be 100 characters or less'),
  description: z
    .string()
    .min(1, 'Size is required')
    .max(500, 'Size must be 500 characters or less'),
  costPerBox: z
    .number({ invalid_type_error: 'Cost per Box is required' })
    .min(0.01, 'Must be a positive value')
    .max(9999999.99, 'Cost per box cannot exceed $9,999,999.99'),
  itemsPerBox: z
    .number({ invalid_type_error: '# of Items per Box is required' })
    .int('Must be a whole number')
    .min(1, 'Must be at least 1')
    .max(999999, '# of Items per Box cannot exceed 999,999'),
  quantity: z
    .number({ invalid_type_error: 'Quantity is required' })
    .int('Must be a whole number')
    .min(1, 'Must be at least 1')
    .max(999999, 'Quantity cannot exceed 999,999'),
  notes: z.string().max(1000, 'Notes must be 1000 characters or less').optional(),
});

type FormData = z.infer<typeof schema>;

const BOMHardwareModal: React.FC<BOMHardwareModalProps> = ({
  open,
  onClose,
  onSuccess,
  mode,
  version,
  bomHardware,
}) => {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [createBOMHardware, { isLoading: isCreating }] = useCreateBOMHardwareMutation();
  const [updateBOMHardware, { isLoading: isUpdating }] = useUpdateBOMHardwareMutation();
  const [deleteBOMHardware, { isLoading: isDeleting }] = useDeleteBOMHardwareMutation();

  const defaultValues: FormData = useMemo(
    () =>
      bomHardware
        ? {
            hardwareType: bomHardware.hardwareType,
            description: bomHardware.description,
            costPerBox: bomHardware.costPerBox,
            itemsPerBox: bomHardware.itemsPerBox,
            quantity: bomHardware.quantity ?? 1,
            notes: bomHardware.notes ?? '',
          }
        : {
            hardwareType: '',
            description: '',
            costPerBox: 0,
            itemsPerBox: 0,
            quantity: 1,
            notes: '',
          },
    [bomHardware]
  );

  const methods = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onTouched',
  });

  const { control, watch, formState, handleSubmit, reset } = methods;
  const watchedValues = watch();

  // Calculated cost per item
  const [costPerItem, setCostPerItem] = useState<number>(0);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recalculate = useCallback((values: FormData) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const cost = values.costPerBox || 0;
      const items = values.itemsPerBox || 0;
      setCostPerItem(items > 0 ? cost / items : 0);
      debounceRef.current = null;
    }, 250);
  }, []);

  useEffect(() => {
    if (open) {
      recalculate(watchedValues);
    }
  }, [open, watchedValues, recalculate]);

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, JSON.stringify(defaultValues), reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCancel = () => {
    if (formState.isDirty) {
      setCancelDialogOpen(true);
    } else {
      handleClose();
    }
  };

  const handleCancelConfirm = () => {
    setCancelDialogOpen(false);
    handleClose();
  };

  const handleDeleteConfirm = async () => {
    if (!bomHardware) return;
    try {
      await deleteBOMHardware({ id: bomHardware.id, versionId: bomHardware.versionId }).unwrap();
      showToast('Hardware Cost deleted', 'success');
      setDeleteDialogOpen(false);
      handleClose();
      onSuccess?.();
    } catch {
      // Error toast shown by API slice
    }
  };

  const onSubmit = handleSubmit(async (data: FormData) => {
    try {
      if (mode === 'add') {
        await createBOMHardware({
          versionId: version.id,
          hardwareType: data.hardwareType.trim(),
          description: data.description.trim(),
          costPerBox: data.costPerBox,
          itemsPerBox: data.itemsPerBox,
          quantity: data.quantity,
          notes: data.notes?.trim() || undefined,
        }).unwrap();
        showToast('Hardware Cost saved successfully', 'success');
      } else if (mode === 'edit' && bomHardware) {
        await updateBOMHardware({
          id: bomHardware.id,
          versionId: bomHardware.versionId,
          hardwareType: bomHardware.hardwareType,
          description: bomHardware.description,
          costPerBox: data.costPerBox,
          itemsPerBox: bomHardware.itemsPerBox,
          quantity: data.quantity,
          notes: data.notes?.trim() || undefined,
        }).unwrap();
        showToast('Hardware Cost saved successfully', 'success');
      }
      handleClose();
      onSuccess?.();
    } catch {
      // Error toast shown by API slice
    }
  });

  const formatCurrency = (val: number) => (val > 0 ? `$${val.toFixed(2)}` : '--');

  const isReadOnly = mode === 'view';
  // In edit mode, identity fields (hardwareType, description, itemsPerBox) cannot be changed
  const isIdentityReadOnly = mode === 'edit' || mode === 'view';
  const isSubmitting = isCreating || isUpdating || isDeleting;

  const modalTitle =
    mode === 'add'
      ? 'Add Hardware Cost'
      : mode === 'edit'
        ? `Edit Hardware Cost - ${bomHardware?.description ?? ''}`
        : `View Hardware Cost - ${bomHardware?.description ?? ''} (Published)`;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { width: 560 } }}
        disableEscapeKeyDown={formState.isDirty}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6">{modalTitle}</Typography>
            <Chip
              label={`${version.status} v${version.versionNumber}.0`}
              size="small"
              color={version.status === 'Draft' ? 'warning' : 'success'}
            />
          </Box>
          <IconButton onClick={handleCancel} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Code (hardwareType) */}
            <Controller
              name="hardwareType"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <TextField
                    {...field}
                    label="Code"
                    fullWidth
                    size="small"
                    disabled={isIdentityReadOnly}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    inputProps={{ maxLength: 100 }}
                    sx={
                      isIdentityReadOnly
                        ? { '& .MuiInputBase-input': { color: 'text.secondary' } }
                        : undefined
                    }
                  />
                  <FormHelperText>
                    Enter the hardware product code or description (e.g., 'Bolt - 1/2x3"')
                  </FormHelperText>
                </Box>
              )}
            />

            {/* Size (description) */}
            <Controller
              name="description"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <TextField
                    {...field}
                    label="Size"
                    fullWidth
                    size="small"
                    disabled={isIdentityReadOnly}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    inputProps={{ maxLength: 500 }}
                    sx={
                      isIdentityReadOnly
                        ? { '& .MuiInputBase-input': { color: 'text.secondary' } }
                        : undefined
                    }
                  />
                  <FormHelperText>
                    Enter hardware size specification (e.g., '1/2x3"')
                  </FormHelperText>
                </Box>
              )}
            />

            {/* Cost per Box */}
            <Controller
              name="costPerBox"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <TextField
                    {...field}
                    label="Cost per Box"
                    fullWidth
                    size="small"
                    type="number"
                    disabled={isReadOnly}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                    onChange={e => field.onChange(parseFloat(e.target.value) || 0)}
                    value={field.value || ''}
                  />
                  <FormHelperText>Price per box of hardware items</FormHelperText>
                </Box>
              )}
            />

            {/* # of Items per Box */}
            <Controller
              name="itemsPerBox"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <TextField
                    {...field}
                    label="# of Items per Box"
                    fullWidth
                    size="small"
                    type="number"
                    disabled={isIdentityReadOnly}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    onChange={e => field.onChange(parseInt(e.target.value, 10) || 0)}
                    value={field.value || ''}
                    sx={
                      isIdentityReadOnly
                        ? { '& .MuiInputBase-input': { color: 'text.secondary' } }
                        : undefined
                    }
                  />
                  <FormHelperText>Number of individual hardware items in each box</FormHelperText>
                </Box>
              )}
            />

            {/* Quantity */}
            <Controller
              name="quantity"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <TextField
                    {...field}
                    label="Quantity"
                    fullWidth
                    size="small"
                    type="number"
                    disabled={isReadOnly}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    onChange={e => field.onChange(parseInt(e.target.value, 10) || 0)}
                    value={field.value || ''}
                  />
                  <FormHelperText>Number of this hardware item used per truss</FormHelperText>
                </Box>
              )}
            />

            {/* Calculated Results */}
            <Box
              sx={{
                p: 2,
                backgroundColor: 'action.hover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Calculated Results
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Cost per Item:
                  </Typography>
                  <Typography variant="body2">
                    {watchedValues.itemsPerBox > 0 ? formatCurrency(costPerItem) : '--'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Notes */}
            <Controller
              name="notes"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <TextField
                    {...field}
                    label="Notes"
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    disabled={isReadOnly}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    inputProps={{ maxLength: 1000 }}
                  />
                  <FormHelperText>Additional notes (optional)</FormHelperText>
                </Box>
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Box>
            {mode === 'edit' && (
              <Button
                color="error"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={isSubmitting}
              >
                Delete
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            {mode !== 'view' && (
              <Button
                variant="contained"
                onClick={onSubmit}
                disabled={!formState.isValid || isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            )}
            {mode === 'view' && (
              <Button variant="contained" onClick={handleClose}>
                Close
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <CancelConfirmDialog
        open={cancelDialogOpen}
        onConfirm={handleCancelConfirm}
        onDismiss={() => setCancelDialogOpen(false)}
        dialogTitle="Discard unsaved changes?"
        dialogContent="Are you sure you want to discard your changes? All entered data will be lost."
        continuePrompt="Keep Editing"
        cancelPrompt="Discard"
      />

      <Dialog open={deleteDialogOpen} onClose={() => !isDeleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete this hardware cost?</DialogTitle>
        <DialogContent>
          <Typography>This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BOMHardwareModal;
